import { asList } from 'shared/utilities/format';

/** Laravel specialist avatar rule: max 2048 KB */
export const SPECIALIST_AVATAR_MAX_BYTES = 2048 * 1024;

export const SPECIALIST_AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp';

const ALLOWED_AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * Normalize API boolean values at the boundary.
 * Returns true, false, or null when unknown/invalid.
 * Never uses Boolean("0") which would be true.
 */
export function normalizeSpecialistBoolean(value) {
  if (value === true || value === false) {
    return value;
  }
  if (value === 1 || value === '1') {
    return true;
  }
  if (value === 0 || value === '0') {
    return false;
  }
  return null;
}

export function normalizeSpecialist(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const {
    avatar_path: _avatarPath,
    ...rest
  } = raw;

  return {
    ...rest,
    is_active: normalizeSpecialistBoolean(raw.is_active),
    sort_order: Number.isFinite(Number(raw.sort_order)) ? Number(raw.sort_order) : null,
    avatar_url: typeof raw.avatar_url === 'string' ? raw.avatar_url : null,
  };
}

export function normalizeSpecialistList(data) {
  return asList(data)
    .map((item) => normalizeSpecialist(item))
    .filter(Boolean);
}

export function filterSpecialistsByStatus(items, status) {
  if (status === 'active') {
    return items.filter((item) => item.is_active === true);
  }
  if (status === 'inactive') {
    return items.filter((item) => item.is_active === false);
  }
  return items;
}

export function sortSpecialists(items) {
  return [...items].sort((left, right) => {
    const orderL = Number.isFinite(left.sort_order) ? left.sort_order : Number.MAX_SAFE_INTEGER;
    const orderR = Number.isFinite(right.sort_order) ? right.sort_order : Number.MAX_SAFE_INTEGER;
    if (orderL !== orderR) {
      return orderL - orderR;
    }
    return Number(left.id || 0) - Number(right.id || 0);
  });
}

export function isActiveSpecialist(item) {
  return item?.is_active === true;
}

export function validateSpecialistAvatarFile(file, t) {
  if (!file) {
    return null;
  }
  if (!ALLOWED_AVATAR_TYPES.has(file.type)) {
    return t('specialists.avatarInvalidType');
  }
  if (file.size > SPECIALIST_AVATAR_MAX_BYTES) {
    return t('specialists.avatarTooLarge');
  }
  return null;
}

export function mergeSpecialistIntoListPayload(data, specialist) {
  if (!data || !specialist) {
    return data;
  }
  const merge = (items) => items.map((item) => (
    Number(item.id) === Number(specialist.id) ? { ...item, ...specialist } : item
  ));
  if (Array.isArray(data)) {
    return merge(data);
  }
  if (Array.isArray(data.items)) {
    return { ...data, items: merge(data.items) };
  }
  if (Array.isArray(data.data)) {
    return { ...data, data: merge(data.data) };
  }
  return data;
}

export function snapshotSpecialistQueries(queryClient) {
  return queryClient.getQueriesData({ queryKey: ['operations', 'specialists'] });
}

export function applySpecialistListSnapshots(queryClient, snapshots) {
  snapshots.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });
}

export async function invalidateSpecialistQueries(queryClient, specialistId) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['operations', 'specialists'] }),
    specialistId != null
      ? queryClient.invalidateQueries({ queryKey: ['operations', 'specialists', String(specialistId)] })
      : Promise.resolve(),
    queryClient.refetchQueries({ queryKey: ['operations', 'specialists'], type: 'active' }),
    specialistId != null
      ? queryClient.refetchQueries({ queryKey: ['operations', 'specialists', String(specialistId)], type: 'active' })
      : Promise.resolve(),
  ]);
}
